import React, { useEffect, useMemo, useState } from "react";
import { app, pages } from "@microsoft/teams-js";
import {
  Label,
  RadioGroup,
  Radio,
  Select,
  Checkbox,
  Text,
  Button,
  Input,
  Combobox,
  Option,
} from "@fluentui/react-components";
import { userApi } from "../redux/api/userApi";
import { ticketApi } from "../redux/api/ticketApi";
import { ITabConfig } from "../types/Config/ITabConfig";
import { clientApi } from "../redux/api/clientApi";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { setConfig } from "@microsoft/teams-js/dist/esm/packages/teams-js/dts/public/pages/config";
import { PartialSpinny, Spinny } from "./Spinny";
import { getUserManager } from "../redux/store";
import { cookieTenantId } from "../settings/authSettings";

const scopeOptions = [
  { value: "team", label: "Team" },
  { value: "group", label: "Group" },
  { value: "client", label: "Client" },
  { value: "projects", label: "Projects (multi)" },
];


const stateList = [
  { key: "new", text: "New" },
  { key: "current", text: "Current" },
  { key: "atrisk", text: "At Risk" },
  { key: "late", text: "Late" },
];

const TabConfig: React.FC = () => {
  // Using Fluent v9 tokens and components; remove legacy Fabric theme usage
  const [tabConfig, setTabConfig] = useState<ITabConfig>({ tabName: "My Tickets", myCallsOnly: true } as ITabConfig);
  const { data: profile, error, isLoading: profileLoagin } = userApi.useGetProfileQuery();
  const { data: clients, isLoading: clientsLoading } = clientApi.useGetClientsQuery();
  const { data: teams, isLoading: teamsLoading } = userApi.useGetTeamsQuery();
  const { data: queues, isLoading: queuesLoading } = userApi.useGetQueuesQuery();
  const { data: projects, isLoading: projectsLoading } = clientApi.useGetProjectListQuery(tabConfig.clientIds ? tabConfig.clientIds[0] : 0, { skip: !tabConfig.clientIds || tabConfig.clientIds.length === 0 });
  const { data: prioritySets, isLoading: prioritySetsLoading } = ticketApi.useGetPrioritySetsQuery();
  const { data: statuses, isLoading: statusesLoading } = ticketApi.useGetStatusesQuery();
  const { data: priorityData, isLoading: priorityDataLoading } = ticketApi.useGetPriorityListQuery(Number(tabConfig.prioritySetId), { skip: !tabConfig.prioritySetId });
  const isLoading = profileLoagin || clientsLoading || teamsLoading || queuesLoading || projectsLoading || prioritySetsLoading || statusesLoading || priorityDataLoading;

  const [selectedScope, setSelectedScope] = useState<string | string[] | undefined>(undefined);
  const [selectedPriority, setSelectedPriority] = useState<string>("all");
  const [selectedStates, setSelectedStates] = useState<Record<string, boolean>>({});
  const [isValid, setIsValid] = useState<boolean>(true);

  const priorityOptions = useMemo(() => {
    if (!priorityData) return [{ value: "all", label: "All" }];
    return [{ value: "all", label: "All" }].concat(
      priorityData.map((p) => ({ value: p.ID.toString(), label: p.Priority_Level }))
    );
  }, [priorityData]);

  const prioritySetsOptions = useMemo(() => {
    if (!prioritySets) return [];
    return [{ value: "all", label: "All" }].concat(prioritySets.map((ps) => ({ value: ps.ID.toString(), label: ps.Name })));
  }, [prioritySets]);

  const statusOptions = useMemo(() => {
    if (!statuses) return [{ value: "all", label: "All" }];
    return statuses?.map((s) => ({ value: s.ID.toString(), label: s.Call_Status }))
      ;
  }, [statuses]);

  const queueOptions = useMemo(() => {
    if (!queues) return [];
    return queues.map((q) => ({ value: q.ID, label: q.Full_Name }));
  }, [queues]);

  const teamOptions = useMemo(() => {
    if (!teams) return [];
    return teams.map((t) => ({ value: t.ID, label: t.Dept_Name }));
  }, [teams]);

  const clientOptions = useMemo(() => {
    if (!clients) return null;
    return clients.map((c) => ({ value: c.ID, label: c.Company_Name }));
  }, [clients]);

  const projectOptions = useMemo(() => {
    if (!projects) return null;
    return projects.map((p) => ({ value: p.ID, label: p.Project_Name }));
  }, [projects]);

  const hasAccess = !profile?.Features?.External_Interface;

  //Load the initial config
  React.useEffect(() => {
    async function loadConfig() {
      await app.initialize();
      const current = await pages.getConfig();

      console.log("Current tab config:", current);

      if (current && current.contentUrl) {
        const url = new URL(current.contentUrl);
        const params = url.searchParams;

        setTabConfig({
          entityId: current.entityId,
          isNewTab: current.entityId ? false : true,
          tabName: current.suggestedDisplayName || "My Tickets",
          myCallsOnly: params.get("myCallsOnly") === "1",
          statusIds: params.get("statusIds") ? params.get("statusIds")!.split(",").map(Number) : [],
          prioritySetId: params.get("prioritySetId") ? Number(params.get("prioritySetId")) : undefined,
          priorityIds: params.get("priorityIds") ? params.get("priorityIds")!.split(",").map(Number) : [],
          clientIds: params.get("clientIds") ? params.get("clientIds")!.split(",").map(Number) : [],
          projectIds: params.get("projectIds") ? params.get("projectIds")!.split(",").map(Number) : [],
          teamIds: params.get("teamIds") ? params.get("teamIds")!.split(",").map(Number) : [],
          queueIds: params.get("queueIds") ? params.get("queueIds")!.split(",").map(Number) : [],
          states: params.get("states") ? params.get("states")!.trim().split(",") : [],
        } as ITabConfig);

      } else {
        setTabConfig({
          entityId: crypto.randomUUID(), //generate an GUID
          isNewTab: true,
          tabName: "Open Tickets",
          myCallsOnly: false,
        } as ITabConfig);
      }
    }

    //runs async
    loadConfig();
  }, []);

  //delay load the team names, queue names, client names, project names
  useEffect(() => {
    console.warn("Updating tab config with display names for selected IDs", projectOptions);
    if (teamOptions && queueOptions && clientOptions && statusOptions && priorityOptions) {
      console.warn("Updating tab config with display names for selected IDs", tabConfig);
      setTabConfig((c) => ({
        ...c,
        teamNames: teamOptions.filter((t) => c.teamIds?.includes(t.value)).map((t) => t.label),
        queueNames: queueOptions.filter((q) => c.queueIds?.includes(q.value)).map((q) => q.label),
        clientNames: clientOptions.filter((cl) => c.clientIds?.includes(cl.value)).map((cl) => cl.label),
        projectNames: projectOptions ? projectOptions.filter((p) => c.projectIds?.includes(p.value)).map((p) => p.label) : c.projectNames,
        statusNames: statusOptions.filter((s) => c.statusIds?.includes(Number(s.value))).map((s) => s.label),
        priorityNames: priorityOptions.filter((s) => c.priorityIds?.includes(Number(s.value))).map((s) => s.label),
        //only the client ids that are available
        clientIds: c.clientIds?.filter(cid => clientOptions.some(co => co.value === cid)),
        projectIds: projectOptions ? c.projectIds?.filter(pid => projectOptions.some(po => po.value === pid)) : c.projectIds,
      }));
    }

  }, [teamOptions, queueOptions, clientOptions, projectOptions, statusOptions, priorityOptions]);


  useEffect(() => {
    app.initialize().then(() => {


      const onSave = (saveEvent: any) => {
        const baseUrl = window.location.origin;
        const tabDesc = tabConfig.teamNames?.map(s => s.trim()).concat(tabConfig.queueNames?.map(s => s.trim()) || [], tabConfig.clientNames?.map(s => s.trim()) || [], tabConfig.projectNames?.map(s => s.trim()) || []).join(",");

        const argsArr =
          [`myCallsOnly=${tabConfig.myCallsOnly ? '1' : '0'}`,
            `&userObjectId={userObjectId}&tabId={entityId}`,
          `&teamIds=${tabConfig.teamIds?.join(",") || ""}`,
          `&queueIds=${tabConfig.queueIds?.join(",") || ""}`,
          `&clientIds=${tabConfig.clientIds?.join(",") || ""}`,
          `&projectIds=${tabConfig.projectIds?.join(",") || ""}`,
          `&statusIds=${tabConfig.statusIds?.join(",") || ""}`,
          `&priorityIds=${tabConfig.priorityIds?.join(",") || ""}`,
          `&prioritySetId=${tabConfig.prioritySetId || ""}`,
          `&states=${tabConfig.states?.map(s => s.trim()).join(",") || ""}`,
          `&priorityNames=${tabConfig.priorityNames?.map(s => s.trim()).join(",") || ""}`,
          `&statusNames=${tabConfig.statusNames?.map(s => s.trim()).join(",") || ""}`,
          `&tabName=${encodeURIComponent(tabConfig.tabName || "My Tickets")}`,
          `&revTenantId=${profile?.Tenant_Uid || ""}`,
          `&description=${encodeURIComponent(tabDesc || '')}`];

        const contentUrl = `${baseUrl}/tab?` + argsArr.join("");

        console.log("Saving tab with URL:", tabDesc, contentUrl, argsArr, tabConfig, profile?.Tenant_Uid);

        pages.config.setConfig({
          suggestedDisplayName: tabConfig.tabName || "My Tickets",
          entityId: tabConfig.entityId,
          contentUrl,
          websiteUrl: contentUrl,
        })
          .then(() => {
            saveEvent.notifySuccess();
          })
          .catch(() => {
            saveEvent.notifyFailure();
          });
      };

      pages.config.registerOnSaveHandler(onSave);

      //Check if the config is valid to enable save
      pages.config.setValidityState(isValid);

      console.log("Setting validity state:", isValid);
    });



    return () => {
      // There's no unregister API for registerOnSaveHandler; leaving cleanup minimal.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, selectedScope, tabConfig, selectedPriority, selectedStates, profile]);

  const scopeRequired = (!tabConfig?.myCallsOnly && !tabConfig.teamIds?.length && !tabConfig.queueIds?.length && !tabConfig.clientIds?.length && !tabConfig.projectIds?.length)
  useEffect(() => {
    // Basic validation: if scope selection required, ensure something is selected
    let valid = tabConfig.tabName.trim().length > 0
    //displayMode === "mytickets" ||

    //must select a scope if not my tickets
    if (!tabConfig?.myCallsOnly && !tabConfig.teamIds?.length && !tabConfig.queueIds?.length && !tabConfig.clientIds?.length && !tabConfig.projectIds?.length) {
      valid = false;
    }

    setIsValid(valid);
    //
  }, [tabConfig]);

  const logout = async () => {
    getUserManager(cookieTenantId).signoutSilent();

    document.location.href = "/config";

  }

  if (!hasAccess)
    return (<div className="alert alert-danger m-3 d-flex align-items-center">
      <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" fill="currentColor" className="bi bi-exclamation-triangle" viewBox="0 0 16 16">
        <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.15.15 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.2.2 0 0 1-.054.06.1.1 0 0 1-.066.017H1.146a.1.1 0 0 1-.066-.017.2.2 0 0 1-.054-.06.18.18 0 0 1 .002-.183L7.884 2.073a.15.15 0 0 1 .054-.057m1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767z" />
        <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
      </svg>
      <div className="ms-2">You do not have access to the tab configuration. Only internal (support) users can configure tabs.</div>
    </div>
    );

  return (
    <div>
      {/* error && "status" in error && error.status === 401 */}
      {error && "status" in error && error.status === 401 && <div className="alert alert-danger m-3 d-flex align-items-center" role="alert">Unauthorized access, please sign in as a different user.
        <Button onClick={logout} className="ms-auto me-auto"><img src="/images/rev-logo-96.png" alt="Revelation logo" style={{ height: 24, marginRight: 8 }} />Sign Out</Button></div>}
      {profileLoagin ? <Spinny text="Loading configuration..." /> :
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>


          <Label><h5>Open {`${profile?.Phrases?.CallPhrase}s`}</h5></Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
            <div className="d-flex flex-column flex-grow-1">
              <Label><h6>Tab Name</h6></Label>

              {!tabConfig.isNewTab ? <div className="alert alert-info p-1 mt-1 m-0 d-flex" role="alert">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-info-circle" viewBox="0 0 16 16">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                </svg><div className="ms-1">Tab names can only be changed in the tab settings menu.</div>
              </div> : null}
              <Input disabled={!tabConfig.isNewTab}
                value={tabConfig.tabName} onChange={(e) => setTabConfig({ ...tabConfig, tabName: e.target.value })} />
            </div>
            <div className="mt-3" style={{ flex: "1 1 auto", minWidth: 280, display: "flex", flexDirection: "column", gap: 8 }}>
              <Label><h6>Display</h6></Label>
              <hr className="m-0" />
              <RadioGroup
                value={tabConfig?.myCallsOnly ? "1" : "0"}
                onChange={(e, data) => {
                  if (data.value === "1")
                    setTabConfig((t) => ({ ...t, myCallsOnly: true, teamIds: [], teamNames: [], queueIds: [], queueNames: [], clientIds: [], clientNames: [], projectIds: [], projectNames: [] }));
                  else
                    setTabConfig((t) => ({ ...t, myCallsOnly: false }));
                }}

              >
                <Radio value="0" label={`Specific Scope (${profile?.Phrases?.Team} / Group / ${profile?.Phrases?.Client} / ${profile?.Phrases?.Project})`}></Radio>
                {/* <Radio value="1" label={`My ${profile?.Phrases?.CallPhrase}s`}></Radio> */}
              </RadioGroup>
              {scopeRequired && <div className="alert alert-danger d-flex align-items-center" role="alert">At least 1 scope requires selection</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Label>{profile?.Phrases?.Team}</Label>
                <Combobox placeholder="Select..."
                  disabled={tabConfig.myCallsOnly}
                  value={tabConfig.teamNames?.map(String).join(", ")}
                  multiselect={true}
                  selectedOptions={tabConfig?.teamIds?.map(String) || []}
                  onOptionSelect={(e, data) => {
                    console.log("Selected team IDs:", data, teamOptions.filter(o => data.selectedOptions.includes(String(o.value))).map(o => o.label));
                    setTabConfig((c) => ({ ...c, teamIds: data.selectedOptions.map(Number), teamNames: teamOptions.filter(o => data.selectedOptions.includes(String(o.value))).map(o => o.label) }))
                  }
                  }
                  aria-label="Team"
                >
                  {teamOptions.map((o) => (
                    <Option key={o.value} value={String(o.value)}>
                      {o.label}
                    </Option >
                  ))}
                </Combobox >
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Label>{profile?.Phrases?.Queue}</Label>
                <Combobox placeholder="Select..."
                  disabled={tabConfig.myCallsOnly}
                  value={tabConfig.queueNames?.map(String).join(", ") || ""}
                  multiselect={true}
                  selectedOptions={tabConfig?.queueIds?.map(String) || []}
                  onOptionSelect={(e, data) => {
                    var queueNames = queueOptions.filter(o => data.selectedOptions.includes(String(o.value))).map(o => o.label);
                    console.log("Selected queue IDs:", data, queueNames);
                    setTabConfig((c) => ({ ...c, queueIds: data.selectedOptions.map(Number), queueNames: queueNames }))
                  }
                  }
                  aria-label="Queue"
                >
                  {queueOptions.map((o) => (
                    <Option key={o.value} value={String(o.value)} >
                      {o.label}
                    </Option >
                  ))}
                </Combobox >
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Label>{profile?.Phrases?.Client}</Label>
                <Combobox placeholder="Select..."
                  disabled={tabConfig.myCallsOnly}
                  value={tabConfig.clientNames?.map(String).join(", ")}
                  selectedOptions={tabConfig?.clientIds?.map(String) || []}
                  multiselect={true}
                  onOptionSelect={(e, data) => {
                    console.log("Selected client IDs:", e, data);
                    var names = clientOptions?.filter(o => data.selectedOptions.includes(String(o.value))).map(o => o.label) || [];
                    setTabConfig((c) => ({
                      ...c, clientIds: data.selectedOptions.map(Number), clientNames: names,
                      projectIds: data.selectedOptions.length === 1 ? c.projectIds : [], //if multiple clients selected, reset project selection since we can only show projects for one client at a time
                      projectNames: data.selectedOptions.length === 1 ? c.projectNames : []
                    }))
                  }
                  }
                  aria-label="Client"
                >
                  {clientOptions?.map((o) => (
                    <Option key={o.value} value={String(o.value)}>
                      {o.label}
                    </Option >
                  ))}
                </Combobox >
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Label>{profile?.Phrases?.Project}</Label>
                <Combobox placeholder={projectsLoading ? "Loading..." : "Select..."}

                  disabled={tabConfig.myCallsOnly || tabConfig?.clientIds?.length !== 1}
                  value={tabConfig.projectNames?.map(String).join(",")}
                  selectedOptions={tabConfig?.projectIds?.map(String) || []}
                  multiselect={true}
                  onOptionSelect={(e, data) => {
                    console.log("Selected project IDs:", data);
                    var names = projectOptions?.filter(o => data.selectedOptions.includes(String(o.value))).map(o => o.label) || [];
                    setTabConfig((c) => ({ ...c, projectIds: data.selectedOptions.map(Number), projectNames: names }))
                  }
                  }
                  aria-label={profile?.Phrases?.Project}
                >
                  {projectOptions?.map((o) => (
                    <Option key={o.value} value={String(o.value)}>
                      {o.label}
                    </Option >
                  ))}
                </Combobox >

              </div>

            </div>

            <div style={{ flex: "1 1 auto", minWidth: 280, display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
              <Label><h6>Filters</h6></Label>
              <hr className="m-0" />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <div style={{ minWidth: 140 }}>
                  <Label>Status</Label>
                  <div><Combobox placeholder="Select..."
                    disabled={tabConfig.myCallsOnly}
                    value={tabConfig.statusNames?.map(String).join(",")}
                    selectedOptions={tabConfig.statusIds?.map(String) || []}
                    multiselect={true}
                    onOptionSelect={(e, data) => {
                      console.log("Selected status IDs:", data);
                      setTabConfig((c) => ({ ...c, statusIds: data.selectedOptions.map(Number), statusNames: statusOptions.filter(o => data.selectedOptions.includes(String(o.value))).map(o => o.label) }))
                    }
                    }
                    aria-label="Status"
                  >
                    {statusOptions.map((o) => (
                      <Option key={o.value} value={String(o.value)}>
                        {o.label}
                      </Option >
                    ))}
                  </Combobox ></div>
                </div>

                <div style={{ minWidth: 140 }}>
                  <Label>{profile?.Phrases?.Priority}</Label>
                  <Select
                    value={tabConfig.prioritySetId}
                    onChange={(e, data) => setTabConfig((c) => ({ ...c, prioritySetId: Number(data.value) }))}
                    aria-label="Priority Set"
                  >
                    {prioritySetsOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                  <Select
                    value={tabConfig.priorityIds?.length === 1 ? String(tabConfig.priorityIds[0]) : "all"}
                    onChange={(e, data) => setTabConfig((c) => ({ ...c, priorityIds: data.value === "all" ? [] : [Number(data.value)] }))}
                    aria-label="Priority"
                  >
                    {priorityOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>


              <div style={{ gap: 8, marginTop: 16 }}>
                <Label><h6>State</h6></Label>
                <div>{stateList.map((s) => (
                  <Checkbox
                    key={s.key}
                    label={s.text}
                    checked={tabConfig.states?.includes(s.key) || false}
                    onChange={(_, checked) => setTabConfig((c) => {
                      const existing = c.states?.map(st => st.trim()).filter(st => st.length > 0) || [];
                      const exists = existing.includes(s.key);
                      const newStates = !exists
                        ? [...existing, s.key]
                        : existing.filter(state => state !== s.key);

                      console.log("State checkbox changed:", s.key, checked, c.states, newStates.join(","));

                      return { ...c, states: newStates };
                    })}
                  />
                ))}</div>
              </div>
            </div>
          </div>
        </div >
      }
    </div >
  );
};

export default TabConfig;
